import * as React from "react";

import { TWENTY_FIVE } from "@/app/constants/constants";

interface UseDataTablePresentationStateProps<TData> {
  data: TData[];
  isLoading: boolean;
  placeholderLimit?: number;
  title: string;
}

interface UseDataTablePresentationStateReturn<TData> {
  tableData: TData[];
}

const createTableId = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^\w]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/**
 * Keeps loading placeholders and rendered rows in sync for shared tables.
 *
 * @param {UseDataTablePresentationStateProps<TData>} props - Presentation inputs for the table body.
 * @returns {UseDataTablePresentationStateReturn<TData>} Stable row data for loading and loaded states.
 */
export const useDataTablePresentationState = <TData>(
  props: UseDataTablePresentationStateProps<TData>,
): UseDataTablePresentationStateReturn<TData> => {
  const { data, isLoading, placeholderLimit = TWENTY_FIVE, title } = props;

  const tableId = React.useMemo(() => createTableId(title), [title]);

  const [placeholderCountByTableId, setPlaceholderCountByTableId] =
    React.useState<Record<string, number>>({});

  const placeholderCount =
    placeholderCountByTableId[tableId] ?? placeholderLimit;

  React.useEffect(() => {
    if (isLoading || data.length === 0) {
      return;
    }

    setPlaceholderCountByTableId((previousCountByTableId) => ({
      ...previousCountByTableId,
      [tableId]: data.length,
    }));
  }, [data.length, isLoading, tableId]);

  const tableData = React.useMemo(() => {
    return isLoading ? (Array(placeholderCount).fill({}) as TData[]) : data;
  }, [data, isLoading, placeholderCount]);

  return {
    tableData,
  };
};
